// toast-provider.js
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import Toaster from './toaster'

const ToastContext = createContext()

const DEFAULT_TOAST = {
  open: false,
  message: '',
  color: 'success',
  icon: 'success',
  duration: 3000,
}

export const ToastProvider = ({ children }) => {
  const [toast, setToast] = useState(DEFAULT_TOAST)

  const showToast = useCallback((message, options = {}) => {
    setToast({
      open: true,
      message,
      color: options.color || 'success',
      icon: options.icon || 'success',
      duration: options.autoHideDuration || 3000,
    })
  }, [])

  const handleClose = useCallback(() => {
    setToast(prev => ({ ...prev, open: false }))
  }, [])

  const value = useMemo(
    () => ({
      showToast,
      success: (msg, opts) =>
        showToast(msg, { ...opts, color: 'success', icon: 'success' }),
      error: (msg, opts) =>
        showToast(msg, { ...opts, color: 'error', icon: 'error' }),
      warning: (msg, opts) =>
        showToast(msg, { ...opts, color: 'warning', icon: 'warning' }),
      info: (msg, opts) =>
        showToast(msg, { ...opts, color: 'info', icon: 'info' }),
    }),
    [showToast]
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster
        open={toast.open}
        message={toast.message}
        onClose={handleClose}
        color={toast.color}
        icon={toast.icon}
        autoHideDuration={toast.duration}
      />
    </ToastContext.Provider>
  )
}

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
